import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatearFecha } from '../utils/fecha';
import { clasificarMora } from '../utils/mora';

const moneda = (n) => '$' + Math.round(n || 0).toLocaleString('es-CO');

// Enlace de WhatsApp a partir del teléfono guardado. Los números colombianos se
// anotan de diez dígitos, pero WhatsApp los quiere con el indicativo del país.
function enlaceWhatsapp(telefono) {
  if (!telefono) return null;
  const digitos = String(telefono).replace(/\D/g, '');
  if (digitos.length === 10) return `https://wa.me/57${digitos}`;
  if (digitos.length >= 11) return `https://wa.me/${digitos}`;
  return null;
}

// El color acompaña al número, no lo sustituye: quien no distinga bien los
// tonos sigue leyendo los días.
function tonoPorAtraso(dias, periodo) {
  if (dias > periodo * 3) return { fondo: '#fee2e2', tinta: '#991b1b' };
  if (dias > periodo * 2) return { fondo: '#ffedd5', tinta: '#9a3412' };
  return { fondo: '#fef3c7', tinta: '#92400e' };
}

function TablaDeMora({ filas, mostrarUltimoPago, mostrarDeuda }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table className="morosos-table" style={{ minWidth: '54rem' }}>
        <thead>
          <tr>
            <th>Atraso</th>
            <th>Cliente</th>
            <th>Teléfono</th>
            <th>Prestado</th>
            {mostrarDeuda ? <th>Debe de interés</th> : <th>Abonado</th>}
            <th>{mostrarUltimoPago ? 'Último pago' : 'Empezó'}</th>
            <th>Fiador</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => {
            const tono = tonoPorAtraso(f.dias_sin_pagar, f.periodo);
            const whatsapp = enlaceWhatsapp(f.phone);
            return (
              <tr key={f.loan_id}>
                <td>
                  <span style={{
                    display: 'inline-block', padding: '3px 10px', borderRadius: '12px',
                    background: tono.fondo, color: tono.tinta,
                    fontWeight: 'bold', fontSize: '13px', whiteSpace: 'nowrap'
                  }}>
                    {f.dias_sin_pagar} días
                  </span>
                </td>
                <td>
                  <strong>{f.name}</strong>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    {f.cedula || 'sin cédula'}
                    {f.address && <> · 📍 {f.address}</>}
                  </div>
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {f.phone
                    ? <a href={`tel:${f.phone}`} style={{ color: '#4338ca' }}>{f.phone}</a>
                    : '-'}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>{moneda(f.amount)}</td>
                {mostrarDeuda ? (
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {/* El cobradiario debe CUOTAS, no interés acumulado. Su
                        `interesPendiente` es la parte de interés del total que
                        todavía no ha cubierto, y está por encima de cero casi
                        toda la vida del préstamo: puesto aquí haría ver como
                        deudor enorme a quien va al día. Lo que se le cobra es
                        lo que trae atrasado. */}
                    {f.estado.modalidad === 'cuota_fija' ? (
                      <>
                        <strong style={{ color: '#b45309' }}>
                          {moneda(f.estado.atrasoEnPlata)}
                        </strong>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                          {f.estado.cuotasAtrasadas} de {f.estado.cuotasTotales} cuotas
                          {' · '}cuota de {moneda(f.estado.cuota)}
                        </div>
                      </>
                    ) : (
                      <>
                        <strong style={{ color: '#b45309' }}>
                          {moneda(f.estado.interesPendiente)}
                        </strong>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                          cobro de {moneda(f.estado.interesPorCobro)}
                        </div>
                      </>
                    )}
                  </td>
                ) : (
                  <td style={{ whiteSpace: 'nowrap' }}>{moneda(f.total_paid)}</td>
                )}
                <td style={{ whiteSpace: 'nowrap' }}>
                  {formatearFecha(mostrarUltimoPago ? f.last_payment : f.start_date)}
                </td>
                <td>
                  {/* El fiador solo importa aquí: cuando el cliente no paga.
                      Por eso se muestra en la fila y no escondido en la ficha
                      del préstamo. */}
                  {f.fiador_nombre ? (
                    <div style={{
                      background: '#f5f3ff', border: '1px solid #ddd6fe',
                      borderRadius: '6px', padding: '6px 9px', fontSize: '12px',
                      color: '#5b21b6', minWidth: '150px'
                    }}>
                      <strong>🤝 {f.fiador_nombre}</strong>
                      {f.fiador_telefono && (
                        <div>
                          <a href={`tel:${f.fiador_telefono}`} style={{ color: '#5b21b6' }}>
                            {f.fiador_telefono}
                          </a>
                        </div>
                      )}
                      {f.fiador_cedula && <div>{f.fiador_cedula}</div>}
                    </div>
                  ) : (
                    <span style={{ color: '#d1d5db', fontSize: '12px' }}>sin fiador</span>
                  )}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {whatsapp && (
                    <a
                      href={whatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`Escribir a ${f.name} por WhatsApp`}
                      style={{ textDecoration: 'none', fontSize: '18px' }}
                    >
                      💬
                    </a>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


function Delinquents() {
  const [ahora, setAhora] = useState([]);
  const [dejaron, setDejaron] = useState([]);
  const [nunca, setNunca] = useState([]);
  const [deMenos, setDeMenos] = useState([]);
  const [cargando, setCargando] = useState(true);

  const cargar = () => {
    setCargando(true);
    axios.get('/api/delinquents')
      .then(res => {
        const grupos = clasificarMora(res.data?.prestamos || []);
        setAhora(grupos.ahora);
        setDejaron(grupos.dejaron);
        setNunca(grupos.nunca);
        setDeMenos(grupos.deMenos);
      })
      .catch(err => console.error('Error cargando la mora:', err))
      .finally(() => setCargando(false));
  };

  useEffect(cargar, []);

  const total = ahora.length + dejaron.length + nunca.length + deMenos.length;

  return (
    <div className="delinquents-page">
      <h2>⚠️ Clientes en mora</h2>

      <button onClick={cargar} className="refresh-btn">🔄 Actualizar</button>

      {cargando ? (
        <p>Cargando...</p>
      ) : total === 0 ? (
        <div className="success-card">
          <h3>✅ Nadie está atrasado</h3>
          <p>Todos tus clientes han abonado dentro de su plazo.</p>
        </div>
      ) : (
        <>
          {/* Los que venían pagando van primero: son los que más se recuperan
              con una llamada, y hasta ahora no aparecían en ninguna lista. */}
          {/* La lista que faltaba: pagan puntual pero de menos, así que por
              fechas están al día y no aparecían en ningún lado. */}
          {/* Lo primero, siempre: es lo único de esta pantalla que se puede
              cobrar HOY. Debajo está la cobranza vieja, que es otra tarea y no
              debe taparlo — con 96 préstamos sin un solo abono desde 2022, el
              del día quedaba invisible. */}
          {ahora.length > 0 && (
            <div className="table-container" style={{ marginBottom: '22px' }}>
              <h3>🔔 Les vence ahora ({ahora.length})</h3>
              <p style={{ color: '#6b7280', fontSize: '14px', marginTop: 0 }}>
                Se les cumplió la fecha y todavía no han abonado. Estos se
                cobran hoy con una llamada. Al registrarles el pago
                desaparecen de aquí.
              </p>
              <TablaDeMora filas={ahora} mostrarUltimoPago mostrarDeuda />
            </div>
          )}

          {deMenos.length > 0 && (
            <div className="table-container" style={{ marginBottom: '22px' }}>
              <h3>⚖️ Pagan de menos ({deMenos.length})</h3>
              <p style={{ color: '#6b7280', fontSize: '14px', marginTop: 0 }}>
                Están al día en fechas, pero abonan menos de lo que se genera y
                el interés se les va acumulando. Ordenados por lo que más deben.
              </p>
              <TablaDeMora filas={deMenos} mostrarUltimoPago mostrarDeuda />
            </div>
          )}

          {dejaron.length > 0 && (
            <div className="table-container" style={{ marginBottom: '22px' }}>
              <h3>🔻 Dejaron de pagar ({dejaron.length})</h3>
              <p style={{ color: '#6b7280', fontSize: '14px', marginTop: 0 }}>
                Venían abonando y se pasaron de su plazo. Ordenados por los que
                llevan más tiempo sin pagar.
              </p>
              <TablaDeMora filas={dejaron} mostrarUltimoPago />
            </div>
          )}

          {nunca.length > 0 && (
            <div className="table-container">
              <h3>⛔ Nunca han pagado ({nunca.length})</h3>
              <p style={{ color: '#6b7280', fontSize: '14px', marginTop: 0 }}>
                Tienen el préstamo abierto y no han hecho ni un solo abono desde
                que empezó.
              </p>
              <TablaDeMora filas={nunca} />
            </div>
          )}

          <div className="info-box" style={{ marginTop: '20px' }}>
            <h3>Cómo se decide quién está en mora</h3>
            <p>
              <strong>Les vence ahora</strong> son los que acaban de cumplir su
              fecha y aún no abonan — hasta un periodo completo de atraso. Van
              arriba porque son los que todavía se cobran con una llamada; el
              resto de la pantalla es cobranza vieja.
            </p>
            <p>
              Se compara cuánto lleva sin abonar contra el plazo de su propio
              préstamo: en uno <strong>diario</strong>, pasar de un día ya es
              atraso; en uno <strong>mensual</strong>, hacen falta más de treinta.
              Por eso un cliente de cuota diaria puede aparecer aquí en una semana
              y uno mensual no.
            </p>
            <p>
              A la lista de <strong>pagan de menos</strong> entra quien está al
              día en fechas pero debe más de un cobro entero de interés
              acumulado. Se exige que pase de un cobro completo para no señalar
              a quien redondea —el que paga «los cien mil» de una cuota de
              96.400—.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export default Delinquents;
